import type {
  AppointmentScope,
  AppointmentStatus,
  AppointmentType,
  AttendeeKind,
  AttendeeRole,
  ConfirmationState,
} from '@pc/db';

export const APPOINTMENT_SCOPE_LABELS: Record<AppointmentScope, string> = {
  org: 'Org-wide',
  store: 'Store',
  user: 'My calendar',
};

export const APPOINTMENT_TYPE_LABELS: Record<AppointmentType, string> = {
  in_person: 'In person',
  phone: 'Phone',
  video: 'Video',
  other: 'Other',
};

export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  scheduled: 'Scheduled',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  completed: 'Completed',
  no_show: 'No-show',
};

export const CONFIRMATION_STATE_LABELS: Record<ConfirmationState, string> = {
  not_required: 'Not required',
  awaiting: 'Awaiting',
  confirmed: 'Confirmed',
  declined: 'Declined',
};

export const ATTENDEE_KIND_LABELS: Record<AttendeeKind, string> = {
  user: 'User',
  staff_contact: 'Staff (external)',
  client: 'Client',
  guest: 'Guest',
};

export const ATTENDEE_ROLE_LABELS: Record<AttendeeRole, string> = {
  host: 'Host',
  participant: 'Participant',
  observer: 'Observer',
};

export const OPEN_STATUSES: AppointmentStatus[] = [
  'scheduled',
  'confirmed',
];

/**
 * Convert a local datetime-input value (e.g. "2026-05-23T14:30") in the
 * given IANA timezone into an ISO string that lands at the right moment.
 *
 * Browsers don't expose a clean way to do this without a library; we use a
 * round-trip with Intl.DateTimeFormat to find the UTC instant that, when
 * displayed in the target zone, yields the wall clock the operator typed.
 */
export function localWallClockToIsoUtc(
  localValue: string,
  ianaZone: string,
): string {
  // Parse "YYYY-MM-DDTHH:MM" naively (no timezone).
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(localValue);
  if (!m) return new Date(localValue).toISOString();
  const [, y, mo, d, hh, mm] = m;
  const yi = Number(y);
  const moi = Number(mo);
  const di = Number(d);
  const hi = Number(hh);
  const mi = Number(mm);

  // First guess: pretend the wall clock is UTC, then correct by the offset.
  const utcGuess = Date.UTC(yi, moi - 1, di, hi, mi, 0);
  // Find what wall clock that instant displays as in the target zone.
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date(utcGuess));
  const lookup: Record<string, string> = {};
  for (const p of parts) lookup[p.type] = p.value;
  const tzY = Number(lookup.year);
  const tzMo = Number(lookup.month);
  const tzD = Number(lookup.day);
  let tzH = Number(lookup.hour);
  if (tzH === 24) tzH = 0;
  const tzMi = Number(lookup.minute);
  const tzAsUtc = Date.UTC(tzY, tzMo - 1, tzD, tzH, tzMi, 0);
  const diff = utcGuess - tzAsUtc;
  return new Date(utcGuess + diff).toISOString();
}

/**
 * Inverse of `localWallClockToIsoUtc`: given an ISO instant + an IANA zone,
 * return a "YYYY-MM-DDTHH:MM" string suitable for an `<input type="datetime-local">`.
 */
export function isoUtcToLocalWallClock(
  isoUtc: string,
  ianaZone: string,
): string {
  const d = new Date(isoUtc);
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const lookup: Record<string, string> = {};
  for (const p of parts) lookup[p.type] = p.value;
  let hh = lookup.hour;
  if (hh === '24') hh = '00';
  return `${lookup.year}-${lookup.month}-${lookup.day}T${hh}:${lookup.minute}`;
}

/**
 * Pretty-format an ISO instant in the appointment's timezone.
 */
export function formatInZone(
  isoUtc: string,
  ianaZone: string,
  opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  },
): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: ianaZone,
    ...opts,
  }).format(new Date(isoUtc));
}
